import type { Locale } from '@app/constants/Locales'

import { setLocale as persistLocale } from '@app/utils/localeManager'
import moment from '@app/utils/moment'
import { promisify } from '@app/utils/promisify'

import { placesSelector } from '@app/store/slices/entities'
import { intlSelector } from '@app/store/slices/intl'
import { setLocaleSlice } from '@app/store/slices/locale'
import { regionsMapSelector } from '@app/store/slices/regions'
import type { ThunkAction } from '@app/store/thunk'
import { createThunk } from '@app/store/thunk'

import { callV2Api } from './api'
import { getCookies } from './contexts/cookies'
import { getIntlService } from './contexts/intlService'
import { getProgress } from './contexts/progress'

export function getState(): ThunkAction<unknown>
export function getState<R>(selector: (state: unknown) => R): ThunkAction<R>
export function getState<R>(selector?: (state: unknown) => R): ThunkAction<R> {
  return (_dispatch, getState) => {
    if (selector) return selector(getState())
    return getState() as any
  }
}

export function fetchRegion(id: string) {
  return createThunk(async (dispatch, getState) => {
    const state = getState()
    const region = regionsMapSelector(state)[id]
    // we check if place exists, to ensure that region was obtained
    // by calling region api and not any other one that has region
    // as relationship
    if (region) {
      const place_id = region.relationships.default_place.data?.id
      if (place_id) {
        const place = placesSelector(state)[place_id]
        if (place) return
      }
    }
    const { getRegionsById } = await import('@app/packages/api/v2/actions/regions')
    return dispatch(callV2Api(getRegionsById(id)))
  })
}

export function setLocale(nextLocale: Locale, persist: boolean = false) {
  return createThunk(async dispatch => {
    const intlService = dispatch(getIntlService())
    const cookies = dispatch(getCookies())
    const intl = await intlService.createIntl(nextLocale)
    moment.locale(nextLocale)

    dispatch(setLocaleSlice(nextLocale, intl))
    if (persist) await persistLocale(nextLocale, cookies)
  })
}

export function getIntl() {
  return createThunk((_dispatch, getState) => intlSelector(getState()))
}

export function withProgress<T>(promise: T, blocking = false) {
  return createThunk(dispatch => dispatch(getProgress()).wrap(promisify(promise), blocking))
}

export function withProgressAction<T>(action: ThunkAction<T>, blocking = false) {
  return createThunk(dispatch => dispatch(getProgress()).wrap(promisify(dispatch(action)), blocking))
}
